import { computed } from "vue";
import { AuthHttpService } from "@/api/http/AuthHttpService";
import type { MeResponse } from "@/api/http/schemas/authSchemas";
import { createGlobalState, useSessionStorage } from "@vueuse/core";
import { useReadonlyWithSetter } from "@/composables/common";
import useOptionsState from "@/composables/state/optionsState";
import usePlatformScreenState from "@/composables/state/platformScreenState";
import config from "@/config";

const useAuthState = createGlobalState(() => {
  const [authToken, setAuthToken] = useReadonlyWithSetter(useSessionStorage("authToken", ""));
  const [loggedInUser, setLoggedInUser] = useReadonlyWithSetter<MeResponse | null>(null);

  const isLoggedIn = computed(() => Boolean(loggedInUser.value));

  const loginWithSecret = async () => {
    const { email, secret } = config;
    const { token } = await new AuthHttpService().loginWithSecret({ email, secret });
    setAuthToken(token);
  };

  const getLoggedInUser = async () => {
    if (!authToken.value) {
      setLoggedInUser(null);
      return;
    }

    const data = await new AuthHttpService().getMe();

    setLoggedInUser(data);
  };

  const logout = () => {
    const { selectedTabId } = usePlatformScreenState();
    selectedTabId.value = null;

    const { setOptions } = useOptionsState();
    setOptions(null);

    const url = new URL(window.location.href);
    url.searchParams.delete("secret");
    url.searchParams.delete("email");
    window.history.replaceState({}, "", url);

    setAuthToken("");
    setLoggedInUser(null);
  };

  return {
    authToken,
    setAuthToken,
    loggedInUser,
    isLoggedIn,
    loginWithSecret,
    logout,
    getLoggedInUser,
    setLoggedInUser,
  };
});

export default useAuthState;
